import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ShieldCheck, Truck, Copy, Store } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const { t, lang, tCategory } = useLang();

  useEffect(() => {
    api.get("/products?limit=8").then((r) => setProducts(r.data));
    api.get("/services?limit=8").then((r) => setServices(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/nouveautes?limit=8").then((r) => setNewArrivals(r.data)).catch(() => {});
  }, []);

  const copyLaunch = () => {
    navigator.clipboard.writeText("LANCEMENT10");
    toast.success(t("home.launch.copied"));
  };

  return (
    <div data-testid="home-page">
      {/* Launch coupon strip — permanent promo banner */}
      <section className="bg-gradient-to-r from-[#C85A32] via-[#D98A2C] to-[#C85A32] text-white" data-testid="launch-strip">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
          <span className="font-bold tracking-wider text-xs sm:text-sm">{t("home.launch.badge")}</span>
          <span className="hidden sm:inline">{t("home.launch.text")}</span>
          <button onClick={copyLaunch} className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-full px-3 py-0.5 font-mono font-bold text-xs tracking-wider transition" data-testid="launch-copy-btn" title={t("home.launch.copy")}>
            LANCEMENT10 <Copy className="w-3 h-3"/>
          </button>
          <span className="hidden md:inline">{t("home.launch.discount")}</span>
          <Link to="/produits" className="underline hover:no-underline font-semibold ml-1" data-testid="launch-cta">{t("home.launch.cta")} →</Link>
        </div>
      </section>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#F5F5F0] grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <span className="label-uppercase text-xs text-[#C85A32]">{t("home.tag")}</span>
            <h1 className="heading font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight text-[#111812] leading-[1.05]">
              {t("home.title.buy")} <span className="text-[#C85A32]">{t("home.title.services")}</span> {t("home.title.dash")}
            </h1>
            <p className="mt-6 text-base text-[#4B5563] max-w-xl leading-relaxed">
              {t("home.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/produits"><Button size="lg" className="pill-btn bg-[#1A3322] hover:bg-[#24472F] text-white rounded-full px-6" data-testid="hero-explore-products">{t("home.cta.products")} <ArrowRight className="ml-2 w-4 h-4"/></Button></Link>
              <Link to="/services"><Button size="lg" variant="outline" className="pill-btn rounded-full border-[#1A3322] text-[#1A3322] hover:bg-[#1A3322] hover:text-white" data-testid="hero-explore-services">{t("home.cta.providers")}</Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-[#4B5563]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1A3322]"/> {t("home.badge.payment")}</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#1A3322]"/> {t("home.badge.delivery")}</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C85A32]"/> {t("home.badge.community")}</div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4 relative z-10">
            <div className="space-y-4">
              <img src="https://images.unsplash.com/photo-1721508490084-1b1de5b230d4?w=500" alt="artisan" className="rounded-lg aspect-[3/4] object-cover" />
              <img src="https://images.unsplash.com/photo-1567361808960-dec9cb578182?w=500" alt="service" className="rounded-lg aspect-square object-cover" />
            </div>
            <div className="space-y-4 pt-8">
              <img src="https://images.unsplash.com/photo-1572402123736-c79526db405a?w=500" alt="marché" className="rounded-lg aspect-square object-cover" />
              <img src="https://images.unsplash.com/photo-1722963295936-03ea25f1d4d2?w=500" alt="art" className="rounded-lg aspect-[3/4] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="label-uppercase text-xs text-[#C85A32]">{t("home.explore")}</span>
            <h2 className="heading font-extrabold text-3xl lg:text-4xl mt-2 text-[#111812]">{t("home.all_categories")}</h2>
          </div>
          <Link to="/categories" className="text-sm text-[#1A3322] hover:text-[#C85A32] font-medium">{t("home.see_all")} →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="categories-grid">
          {categories.slice(0, 12).map((c) => (
            <Link key={c.id} to={`/recherche?category_id=${c.id}&kind=${c.kind}`}
              className={`group relative overflow-hidden bg-white border border-[#1A3322]/10 hover:border-[#1A3322] rounded-lg transition-all ${c.image_url ? "aspect-[4/5]" : "p-5"}`}
              data-testid={`category-tile-${c.slug}`}>
              {c.image_url ? (
                <>
                  <img src={c.image_url} alt={tCategory(c)} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"/>
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <p className="font-extrabold text-base leading-tight drop-shadow-lg">{tCategory(c)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-white/80 mt-1">{c.kind === "service" ? t("home.cat.service") : c.kind === "both" ? t("home.cat.mixed") : t("home.cat.product")}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-9 h-9 rounded-md bg-[#F5F5F0] flex items-center justify-center mb-3">
                    <span className="text-[#1A3322] font-bold text-sm">{tCategory(c)[0]}</span>
                  </div>
                  <p className="font-semibold text-sm text-[#111812]">{tCategory(c)}</p>
                  <p className="text-xs text-[#4B5563] mt-1 uppercase tracking-wider">{c.kind === "service" ? t("home.cat.service") : c.kind === "both" ? t("home.cat.mixed") : t("home.cat.product")}</p>
                </>
              )}
            </Link>
          ))}
        </div>
      </section>

      {/* Promo banner */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-lg bg-[#1A3322] text-white p-10 lg:p-14 grid lg:grid-cols-2 items-center gap-8">
          <div>
            <span className="label-uppercase text-xs text-[#D98A2C]">{t("home.new_badge")}</span>
            <h3 className="heading font-extrabold text-3xl lg:text-4xl mt-3">{t("home.open_shop_title")}</h3>
            <p className="mt-4 text-white/70 max-w-md">{t("home.open_shop_desc")}</p>
            <Link to="/inscription/vendeur"><Button className="mt-6 pill-btn bg-[#D98A2C] hover:bg-[#C85A32] text-white rounded-full" data-testid="banner-become-seller">{t("home.become_seller")}</Button></Link>
          </div>
          <div className="hidden lg:block relative h-56">
            <img src="https://images.unsplash.com/photo-1618574760337-2750f6251d20?w=800" className="absolute right-0 top-0 w-64 h-56 object-cover rounded-lg" alt="atelier"/>
          </div>
        </div>
      </section>

      {/* Landing Marketing — Devenez vendeur SUKANACOOP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 sm:py-20" data-testid="seller-landing-section">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="label-uppercase text-xs text-[#C85A32]">{t("home.sellers_landing.eyebrow")}</span>
          <h2 className="heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-3 text-[#111812]">
            {t("home.sellers_landing.title")}
          </h2>
          <p className="text-[#4B5563] mt-4 text-base sm:text-lg leading-relaxed">
            {t("home.sellers_landing.subtitle")}
          </p>
          <div className="inline-flex items-center gap-2 mt-6 bg-gradient-to-r from-[#C85A32] to-[#D98A2C] text-white px-5 py-2 rounded-full text-sm font-bold shadow-md">
            🎁 {t("home.sellers_landing.launch_offer")}
          </div>
        </div>

        {/* 4 Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {[
            { emoji: "🌍", key: "visibility" },
            { emoji: "💸", key: "commission" },
            { emoji: "🚚", key: "logistics" },
            { emoji: "📱", key: "instant_pay" },
          ].map((b) => (
            <div key={b.key} className="bg-white border border-[#1A3322]/10 rounded-2xl p-6 hover:border-[#C85A32] hover:shadow-md transition-all" data-testid={`benefit-${b.key}`}>
              <div className="w-12 h-12 rounded-xl bg-[#F5F5F0] flex items-center justify-center text-2xl mb-4">{b.emoji}</div>
              <p className="heading font-extrabold text-lg text-[#111812] mb-2">{t(`home.sellers_landing.benefit.${b.key}.title`)}</p>
              <p className="text-sm text-[#4B5563] leading-relaxed">{t(`home.sellers_landing.benefit.${b.key}.desc`)}</p>
            </div>
          ))}
        </div>

        {/* Pricing summary */}
        <div className="bg-gradient-to-br from-[#1A3322] via-[#24472F] to-[#1A3322] rounded-2xl p-8 sm:p-10 text-white">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <span className="label-uppercase text-xs text-[#D98A2C]">{t("home.sellers_landing.plans_eyebrow")}</span>
              <h3 className="heading font-extrabold text-2xl sm:text-3xl mt-2">{t("home.sellers_landing.plans_title")}</h3>
            </div>
            <Link to="/vendeur/abonnement">
              <Button variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1A3322] rounded-full" data-testid="landing-see-plans">
                {t("home.sellers_landing.see_all_plans")} →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { slug: "free", price: "$0", accent: false },
              { slug: "standard", price: "$5", accent: true },
              { slug: "pro", price: "$15", accent: false },
            ].map((p) => (
              <div key={p.slug}
                   className={`rounded-xl p-5 border ${p.accent ? "bg-white text-[#1A3322] border-[#D98A2C]" : "bg-white/5 border-white/10"}`}
                   data-testid={`landing-plan-${p.slug}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className={`heading font-extrabold text-lg ${p.accent ? "text-[#1A3322]" : "text-white"}`}>{t(`home.sellers_landing.plan.${p.slug}.name`)}</p>
                  {p.accent && <span className="text-[10px] bg-[#C85A32] text-white font-bold px-2 py-0.5 rounded-full">POPULAIRE</span>}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className={`heading font-extrabold text-3xl ${p.accent ? "text-[#1A3322]" : "text-white"}`}>{p.price}</span>
                  {p.price !== "$0" && <span className={`text-sm ${p.accent ? "text-[#4B5563]" : "text-white/70"}`}>/{t("home.sellers_landing.month")}</span>}
                </div>
                <p className={`text-sm ${p.accent ? "text-[#4B5563]" : "text-white/70"}`}>{t(`home.sellers_landing.plan.${p.slug}.tagline`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-12">
          <p className="text-sm text-[#4B5563] mb-4">{t("home.sellers_landing.cta_hint")}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/inscription/vendeur">
              <Button className="pill-btn bg-[#C85A32] hover:bg-[#D98A2C] text-white rounded-full h-12 px-8 text-base font-bold" data-testid="landing-cta-signup">
                {t("home.sellers_landing.cta_signup")}
              </Button>
            </Link>
            <Link to="/boutiques">
              <Button variant="outline" className="rounded-full h-12 px-6 text-base border-[#1A3322]/20" data-testid="landing-cta-see-shops">
                {t("home.sellers_landing.cta_see_shops")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Nouveautés SUKANACOOP — mix pinned + latest arrivals */}
      {newArrivals.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="home-new-arrivals">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="label-uppercase text-xs text-[#C85A32]">{t("home.new_arrivals.eyebrow") || "✨ Fraîchement arrivé"}</span>
              <h2 className="heading font-extrabold text-3xl lg:text-4xl mt-2 text-[#111812]">{t("home.new_arrivals.title") || "Nouveautés SUKANACOOP"}</h2>
              <p className="text-sm text-[#4B5563] mt-1">{t("home.new_arrivals.subtitle") || "Les derniers produits et services ajoutés sur la marketplace."}</p>
            </div>
            <Link to="/produits?sort=new" className="text-sm text-[#1A3322] hover:text-[#C85A32] font-medium hidden sm:inline">{t("home.new_arrivals.see_all") || "Voir tout"} →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {newArrivals.map((p) => (
              <div key={p.id} className="relative" data-testid={`new-arrival-${p.id}`}>
                {p.featured_as_new && (
                  <span className="absolute z-10 top-2 left-2 bg-[#C85A32] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md" data-testid={`new-badge-${p.id}`}>Nouveau</span>
                )}
                <ProductCard p={p} kind={p.kind || "product"} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Featured products */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="label-uppercase text-xs text-[#C85A32]">{t("home.featured")}</span>
            <h2 className="heading font-extrabold text-3xl lg:text-4xl mt-2">{t("home.featured_products")}</h2>
          </div>
          <Link to="/produits" className="text-sm text-[#1A3322] hover:text-[#C85A32] font-medium">{t("home.all_products")} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="featured-products">
          {products.map((p) => <ProductCard key={p.id} p={p} kind="product" />)}
        </div>
      </section>

      {/* Featured services */}
      <section className="bg-[#F5F5F0] py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="label-uppercase text-xs text-[#C85A32]">{t("home.highlight")}</span>
              <h2 className="heading font-extrabold text-3xl lg:text-4xl mt-2">{t("home.featured_services")}</h2>
            </div>
            <Link to="/services" className="text-sm text-[#1A3322] hover:text-[#C85A32] font-medium">{t("home.all_services")} →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="featured-services">
            {services.map((p) => <ProductCard key={p.id} p={p} kind="service" />)}
          </div>
        </div>
      </section>
    </div>
  );
}
